import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import Select from "react-select";

// Map applications to origin enum
export const APPLICATIONS = [
  { value: "", label: "All", origin: null },
  { value: "device", label: "Device", origin: 2 },
  { value: "app", label: "App", origin: 1 },
];

export const APP_STATUS = [
  { value: "", label: "All Status" },
  { value: "true", label: "Running" },
  { value: "false", label: "Stopped" },
];

// Debounce hook
const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const DeviceHeartbeatFilter = ({ onFilterChange, isLoading }) => {
  const [macId, setMacId] = useState("");
  const [applicationName, setApplicationName] = useState("");
  const [isAppRunning, setIsAppRunning] = useState("");
  const [liveMode, setLiveMode] = useState(true);

  const debouncedMacId = useDebounce(macId, 1500);
  const prevMacIdRef = useRef("");

  useEffect(() => {
    // Map applicationName to origin enum
    const selectedApp = APPLICATIONS.find(
      (app) => app.value === applicationName,
    );
    const origin = selectedApp ? selectedApp.origin : null;

    onFilterChange({
      macId: macId.trim(),
      applicationName: applicationName.trim(),
      isAppRunning: isAppRunning === "" ? null : isAppRunning === "true",
      liveMode,
      origin,
    });
  }, [
    debouncedMacId,
    applicationName,
    isAppRunning,
    liveMode,
    onFilterChange,
    macId,
  ]);

  const handleClearFilters = () => {
    setMacId("");
    setApplicationName("");
    setIsAppRunning("");
    setLiveMode(true);
  };

  return (
    <Row className="mb-3">
      <Col lg="12">
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-light">
            <Row className="align-items-center justify-content-between w-100">
              <Col md="auto">
                <h5 className="mb-0 d-flex align-items-center gap-2">
                  <i className="bx bx-filter-alt"></i> Search & Filter
                </h5>
              </Col>

              <Col md="auto" className="d-flex gap-2">
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={handleClearFilters}
                  disabled={isLoading}
                  title="Clear all filters"
                >
                  <i className="bx bx-reset me-2"></i>Clear All
                </Button>
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            <Row className="g-3">
              <Col md="3">
                <FormGroup className="mb-0">
                  <Label for="macId" className="small fw-bold">
                    MAC ID
                  </Label>
                  <div className="input-group">
                    <Input
                      type="text"
                      id="macId"
                      placeholder="Enter MAC ID"
                      value={macId}
                      onChange={(e) => setMacId(e.target.value)}
                      disabled={isLoading}
                    />
                    {macId && (
                      <Button
                        color="light"
                        outline
                        size="sm"
                        onClick={() => setMacId("")}
                        disabled={isLoading}
                      >
                        <i className="bx bx-x"></i>
                      </Button>
                    )}
                  </div>
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup className="mb-0">
                  <Label className="small fw-bold">Application Name</Label>
                  <Select
                    options={APPLICATIONS}
                    value={
                      APPLICATIONS.find(
                        (app) => app.value === applicationName,
                      ) || null
                    }
                    onChange={(selected) =>
                      setApplicationName(selected?.value || "")
                    }
                    isDisabled={isLoading}
                    placeholder="Select App"
                    isClearable
                  />
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup className="mb-0">
                  <Label className="small fw-bold">App Status</Label>
                  <Select
                    options={APP_STATUS}
                    value={APP_STATUS.find(
                      (status) => status.value === isAppRunning,
                    )}
                    onChange={(selected) =>
                      setIsAppRunning(selected?.value || "")
                    }
                    isDisabled={isLoading}
                  />
                </FormGroup>
              </Col>

              <Col md="3">
                <FormGroup className="mb-0">
                  <Label className="small fw-bold">Fetch Mode</Label>
                  <div className="btn-group w-100" role="group">
                    <Button
                      size="sm"
                      outline
                      color={liveMode ? "primary" : "secondary"}
                      onClick={() => setLiveMode(true)}
                      active={liveMode}
                      disabled={isLoading}
                      className="flex-grow-1"
                    >
                      Live (8s)
                    </Button>
                    <Button
                      size="sm"
                      outline
                      color={!liveMode ? "primary" : "secondary"}
                      onClick={() => setLiveMode(false)}
                      active={!liveMode}
                      disabled={isLoading}
                      className="flex-grow-1"
                    >
                      Normal (20s)
                    </Button>
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default DeviceHeartbeatFilter;
